<template>
  <div class="markdown-content" ref="markdownContainer" v-html="renderedHtml"></div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, h, render, inject, getCurrentInstance } from 'vue';
import { sanitizeAndParse } from '~/utils/message';

import useDocuments from '~/composables/useDocuments';

import InfoTooltip from '~/components/conversation/chatMessage/InfoTooltip';
import { buildCitationDictFromPlaceholders as buildCitationDict } from '~/utils/citation-placeholders';
import { getSecondOrderReferencesBySnippet, isUnsubstantiatedCitation } from '~/utils/citation-second-order';
import { MAX_INDIVIDUAL_BULLETS } from '~/utils/citation-dom';
import { getMessageReferencesData } from '~/utils/citation-messages';
import { resolveCitationSourceInfo } from '~/utils/citation-source';

export default {
  name: 'TextMessageWithTooltip',
  props: {
    message: {
      type: Object,
      default: () => ({}),
    },
    // Text of a single TEXT/ERROR chunk to render; when omitted, falls back to the
    // legacy `message.message` field (messages that never went through chunked streaming).
    content: {
      type: String,
      default: undefined,
    },
  },
  setup(props) {
    const currentInstance = getCurrentInstance();
    const arcaneApiGateway = inject('arcaneApiGateway');
    const { openDocumentFromTitle, getDocumentThumbs } = useDocuments();

    const markdownContainer = ref(null);

    const renderedHtml = computed(() =>
      sanitizeAndParse(props.content !== undefined ? props.content : props.message.message, props.message.isTyping),
    );

    const handleTooltipTitle = async (payload, id) => {
      await openDocumentFromTitle(payload, id);
    };

    const loadTooltipThumb = async (id, page) => {
      const thumb = await getDocumentThumbs({
        service: arcaneApiGateway,
        documents: [{ id: id, pages: [page] }],
      });

      return thumb[0]?.pages[page]?.small || '';
    };

    const isCitationUnsubstantiated = (id, snippetIndex, page) => {
      const { references } = getMessageReferencesData(props.message);

      return isUnsubstantiatedCitation({ references, id, snippetIndex, page });
    };

    // Groups sibling elements matching `selector` that sit next to each other, separated only by
    // whitespace/punctuation — adjacent citation markers a reader perceives as one citation spot.
    // Mirrors the grouping citation-dom.js's transformDocReferences uses for authoring preview.
    const isSkippableText = (n) =>
      n?.nodeType === Node.TEXT_NODE && /^[\s.,\u00A0\u202F\u2009]+$/.test(n.nodeValue || '');

    const groupConsecutiveElements = (root, selector) => {
      const all = Array.from(root.querySelectorAll(selector));
      const grouped = new Set();
      const groups = [];

      all.forEach((el) => {
        if (grouped.has(el)) return;

        const group = [el];
        grouped.add(el);
        let p = el.nextSibling;
        while (p) {
          if (p.nodeType === 1 && p.matches?.(selector) && !grouped.has(p)) {
            group.push(p);
            grouped.add(p);
            p = p.nextSibling;
            continue;
          }
          if (isSkippableText(p)) {
            p = p.nextSibling;
            continue;
          }
          break;
        }
        groups.push(group);
      });

      return groups;
    };

    // Raw second-order entries for one placeholder, defensively deduped by the same key used for
    // numbering (text preferred over the coordinate-based referenceId), in case the raw data
    // repeats an entry.
    const getSecondOrderRefsForPlaceholder = (ph, allReferences) => {
      const id = ph.getAttribute('data-id');
      const snippetIndex = ph.getAttribute('data-index');
      const page = ph.getAttribute('data-page');
      const snippetKey = `${page}-${snippetIndex}`;

      const rawRefs = allReferences
        .filter((ref) => ref.externalId === id)
        .flatMap((ref) => getSecondOrderReferencesBySnippet(ref)[snippetKey] || []);

      const seenKeys = new Set();
      const refs = rawRefs.filter((ref) => {
        const key = ref.text || ref.referenceId || '';
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });

      return { id, page, snippetIndex, refs };
    };

    const updateTooltips = () => {
      const el = markdownContainer.value;
      if (!el) return;

      const placeholders = el.querySelectorAll('.citation-placeholder');
      // getMessageReferencesData is the shared source of truth for resolving a message's
      // references regardless of legacy vs new-agent shape — do not reimplement that lookup here.
      const { references: allReferences } = getMessageReferencesData(props.message);
      const dict = buildCitationDict(placeholders, allReferences);

      const buildSourceData = (id, snippetIndex, page) =>
        resolveCitationSourceInfo({
          externalId: id,
          unsubstantiated: isCitationUnsubstantiated(id, snippetIndex, page),
        });

      const groups = groupConsecutiveElements(el, '.citation-placeholder');

      groups.forEach((group) => {
        const primary = group[0];

        // Every placeholder in the group ends up represented by exactly one kind of entry:
        // a real second-order ref (isPlain: false, has referenceId/refNum/text) if its snippet has
        // second-order data, or a single "plain" entry (isPlain: true) representing the citation
        // itself if it doesn't. Without this, a placeholder that contributes no second-order refs
        // would be silently dropped whenever it shares a group with one that does.
        const combinedRefs = [];
        const seenCombinedKeys = new Set();
        const plainEntries = [];
        const seenPlainKeys = new Set();

        group.forEach((ph) => {
          const { id, page, snippetIndex, refs } = getSecondOrderRefsForPlaceholder(ph, allReferences);

          if (refs.length) {
            refs.forEach((ref) => {
              const key = `${id}-${ref.text || ref.referenceId || ''}`;
              if (seenCombinedKeys.has(key)) return;
              seenCombinedKeys.add(key);
              combinedRefs.push({ id, page, snippetIndex, ...ref, isPlain: false });
            });
            return;
          }

          // No second-order data for this placeholder's snippet — same doc+snippet collapses to
          // one plain badge instead of stacking visible duplicates.
          const plainKey = `${id}-${snippetIndex}`;
          if (seenPlainKeys.has(plainKey)) return;
          seenPlainKeys.add(plainKey);
          plainEntries.push({ id, page, snippetIndex, isPlain: true });
        });

        // One label-dict key scheme per entry kind (matches how buildCitationDict/dict assigns
        // labels for each). Merging both kinds and sorting by the label actually shown keeps mixed
        // groups (some placeholders with second-order data, some without) reading left-to-right in
        // increasing numeric order, same as a group made up of only one kind.
        const keyFor = (entry) =>
          entry.isPlain ? `${entry.id}-${entry.snippetIndex}` : `${entry.id}-${entry.text || entry.referenceId || ''}`;

        const prioritized = [...combinedRefs, ...plainEntries].sort(
          (a, b) => (dict[keyFor(a)] ?? Infinity) - (dict[keyFor(b)] ?? Infinity),
        );

        const visibleRefs = prioritized.slice(0, MAX_INDIVIDUAL_BULLETS);
        const overflowRefs = prioritized.slice(MAX_INDIVIDUAL_BULLETS);

        const vnodes = visibleRefs.map((entry) => {
          const { id, page, snippetIndex, refNum, referenceId, isPlain } = entry;
          const vnode = h(InfoTooltip, {
            id,
            page,
            snippetIndex,
            ...(isPlain ? {} : { refNum, referenceId }),
            messageData: props.message,
            label: dict[keyFor(entry)],
            source: buildSourceData(id, snippetIndex, page),
            onTitleClicked: (payload) => handleTooltipTitle(payload, id),
            onGetTooltipThumb: loadTooltipThumb,
          });
          // Preserve app-level provides (vue-i18n, store, etc.) for programmatically mounted tooltips.
          vnode.appContext = currentInstance?.appContext;
          return vnode;
        });

        if (overflowRefs.length) {
          const { id, page, snippetIndex } = overflowRefs[0];
          const overflowVnode = h(InfoTooltip, {
            id,
            page,
            snippetIndex,
            messageData: props.message,
            source: buildSourceData(id, snippetIndex, page),
            overflowEntries: overflowRefs.map((entry) => ({
              refNum: entry.refNum,
              referenceId: entry.referenceId,
              label: dict[keyFor(entry)],
            })),
            onTitleClicked: (payload) => handleTooltipTitle(payload, id),
            onGetTooltipThumb: loadTooltipThumb,
          });
          overflowVnode.appContext = currentInstance?.appContext;
          vnodes.push(overflowVnode);
        }

        render(h('span', { style: 'display: contents' }, vnodes), primary);
        // Every other placeholder in the group has been folded into the combined badge cluster
        // rendered on `primary` — clear their own mount points so they don't also render.
        group.slice(1).forEach((ph) => render(null, ph));
      });
    };

    onMounted(() => {
      nextTick(() => {
        updateTooltips();
      });
    });

    watch(
      () => [props.message?.data?.length, props.content],
      () => {
        nextTick(() => {
          updateTooltips();
        });
      },
      { deep: true },
    );

    return {
      markdownContainer,
      renderedHtml,
    };
  },
};
</script>

<style scoped lang="scss">
.markdown-content--loading {
  font-weight: bold;
  color: $bb-neutral-1 !important;
}

.tool-error-text {
  color: $bb-interface-5 !important;
  font-weight: bold;
}

.markdown-content--thinking {
  font-size: 13px !important;
  margin-top: -2px;
  color: #6a6765;
}

.markdown-content {
  word-break: break-word;
  font-size: 15px;
  line-height: $bb-spacing-5;
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;

  :deep(a) {
    color: $bb-primary-3;
  }

  :deep(ul),
  :deep(ol) {
    padding-left: $bb-spacing-6;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-3;

    //&:not(:last-child) {
    // padding-bottom: $bb-spacing-3;
    //}
  }

  :deep(table) {
    border-collapse: collapse;
    outline: 1px solid #ccc;
    outline-offset: -1px;
    border-radius: 16px;
  }

  :deep(th) {
    background-color: $bb-neutral-6;
  }

  :deep(th),
  :deep(td) {
    vertical-align: middle;
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
    padding: $bb-spacing-2 $bb-spacing-4;
    border-right: 1px solid $bb-neutral-4;
    border-bottom: 1px solid $bb-neutral-4;
  }

  :deep(tr:last-child td) {
    border-bottom: 0;
  }

  :deep(th:last-child),
  :deep(td:last-child) {
    border-right: 0;
  }

  :deep(.cursor) {
    display: inline-block;
    margin-left: $bb-spacing-1;
    width: 2px;
    height: 1em;
    background-color: $bb-primary-3;
    animation: blink-animation 1s steps(20, start) infinite;
    @keyframes blink-animation {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }
  }
}
</style>
